import { z } from "zod";
import { jobOfferChainIdSchema, jobOfferIdSchema } from "./job-offer-schemas";
import { jobIdSchema } from "./job-schemas";
import { messageThreadIdSchema } from "./message-schemas";

export const INBOX_NOTIFICATION_PAGE_LIMIT = 25;

export const inboxNotificationIdSchema = jobIdSchema;
export const inboxNotificationCategoryCodeSchema = z.enum([
  "JOB_APPLICATION",
  "JOB_BOOKING",
  "JOB_MESSAGE",
  "JOB_OFFER",
  "JOB_COMPLETION",
  "JOB_REVIEW",
]);
export const inboxNotificationTemplateCodeSchema = z.enum([
  "CUSTOMER_JOB_APPLICATION_RECEIVED",
  "CUSTOMER_FIXED_JOB_BOOKED",
  "CUSTOMER_JOB_BOOKING_CANCELLED",
  "CLEANER_FIXED_JOB_SELECTED",
  "CLEANER_FIXED_JOB_NOT_SELECTED",
  "CLEANER_JOB_BOOKING_CANCELLED",
  "CUSTOMER_JOB_MESSAGE_RECEIVED",
  "CLEANER_JOB_MESSAGE_RECEIVED",
  "CUSTOMER_NEGOTIABLE_JOB_BOOKED",
  "CLEANER_NEGOTIABLE_JOB_BOOKED",
  "CUSTOMER_JOB_OFFER_RECEIVED",
  "CLEANER_JOB_OFFER_RECEIVED",
  "CUSTOMER_JOB_OFFER_ACCEPTED",
  "CLEANER_JOB_OFFER_ACCEPTED",
  "CUSTOMER_JOB_OFFER_DECLINED",
  "CLEANER_JOB_OFFER_DECLINED",
  "CUSTOMER_JOB_OFFER_WITHDRAWN",
  "CLEANER_JOB_OFFER_WITHDRAWN",
  "CUSTOMER_JOB_OFFER_CLOSED",
  "CLEANER_JOB_OFFER_CLOSED",
  "CUSTOMER_JOB_OFFER_EXPIRED",
  "CLEANER_JOB_OFFER_EXPIRED",
  "CUSTOMER_JOB_COMPLETION_REVIEW_REQUIRED",
  "CLEANER_JOB_COMPLETION_APPROVED",
  "CLEANER_JOB_COMPLETION_DISPUTED",
  "CUSTOMER_JOB_REVIEW_RECEIVED",
  "CLEANER_JOB_REVIEW_RECEIVED",
  "CLEANER_PROVIDER_NO_SHOW_REPORTED",
]);
export const inboxNotificationCursorSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/u, "Cursor must use base64url characters");

const isoDateTimeSchema = z.string().datetime({ offset: true });
const versionNoSchema = z.number().int().min(1);
const unreadCountSchema = z.number().int().min(0);
const commonNotificationFields = {
  id: inboxNotificationIdSchema,
  jobId: jobIdSchema,
  createdAt: isoDateTimeSchema,
  readAt: isoDateTimeSchema.nullable(),
  versionNo: versionNoSchema,
};

const customerApplicationNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_APPLICATION"),
  templateCode: z.literal("CUSTOMER_JOB_APPLICATION_RECEIVED"),
});
const customerBookingNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_BOOKING"),
  templateCode: z.literal("CUSTOMER_FIXED_JOB_BOOKED"),
});
const customerBookingCancelledNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_BOOKING"),
  templateCode: z.literal("CUSTOMER_JOB_BOOKING_CANCELLED"),
});
const cleanerSelectedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_BOOKING"),
  templateCode: z.literal("CLEANER_FIXED_JOB_SELECTED"),
});
const cleanerNotSelectedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_APPLICATION"),
  templateCode: z.literal("CLEANER_FIXED_JOB_NOT_SELECTED"),
});
const cleanerBookingCancelledNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_BOOKING"),
  templateCode: z.literal("CLEANER_JOB_BOOKING_CANCELLED"),
});
const customerCompletionReviewRequiredNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_COMPLETION"),
  templateCode: z.literal("CUSTOMER_JOB_COMPLETION_REVIEW_REQUIRED"),
});
const cleanerCompletionApprovedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_COMPLETION"),
  templateCode: z.literal("CLEANER_JOB_COMPLETION_APPROVED"),
});
const cleanerCompletionDisputedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_COMPLETION"),
  templateCode: z.literal("CLEANER_JOB_COMPLETION_DISPUTED"),
});
const customerReviewReceivedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_REVIEW"),
  templateCode: z.literal("CUSTOMER_JOB_REVIEW_RECEIVED"),
});
const cleanerReviewReceivedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_REVIEW"),
  templateCode: z.literal("CLEANER_JOB_REVIEW_RECEIVED"),
});
const cleanerProviderNoShowReportedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_BOOKING"),
  templateCode: z.literal("CLEANER_PROVIDER_NO_SHOW_REPORTED"),
});
const customerMessageNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_MESSAGE"),
  templateCode: z.literal("CUSTOMER_JOB_MESSAGE_RECEIVED"),
  threadId: messageThreadIdSchema,
});
const cleanerMessageNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_MESSAGE"),
  templateCode: z.literal("CLEANER_JOB_MESSAGE_RECEIVED"),
  threadId: messageThreadIdSchema,
});
const customerNegotiableBookingNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_BOOKING"),
  templateCode: z.literal("CUSTOMER_NEGOTIABLE_JOB_BOOKED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const cleanerNegotiableBookingNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_BOOKING"),
  templateCode: z.literal("CLEANER_NEGOTIABLE_JOB_BOOKED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const customerOfferReceivedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CUSTOMER_JOB_OFFER_RECEIVED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const cleanerOfferReceivedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CLEANER_JOB_OFFER_RECEIVED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const customerOfferAcceptedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CUSTOMER_JOB_OFFER_ACCEPTED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const cleanerOfferAcceptedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CLEANER_JOB_OFFER_ACCEPTED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const customerOfferDeclinedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CUSTOMER_JOB_OFFER_DECLINED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const cleanerOfferDeclinedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CLEANER_JOB_OFFER_DECLINED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const customerOfferWithdrawnNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CUSTOMER_JOB_OFFER_WITHDRAWN"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const cleanerOfferWithdrawnNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CLEANER_JOB_OFFER_WITHDRAWN"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const customerOfferClosedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CUSTOMER_JOB_OFFER_CLOSED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const cleanerOfferClosedNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CLEANER_JOB_OFFER_CLOSED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const customerOfferExpiredNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CUSTOMER_JOB_OFFER_EXPIRED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});
const cleanerOfferExpiredNotificationSchema = z.strictObject({
  ...commonNotificationFields,
  categoryCode: z.literal("JOB_OFFER"),
  templateCode: z.literal("CLEANER_JOB_OFFER_EXPIRED"),
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
});

export const inboxNotificationSchema = z
  .discriminatedUnion("templateCode", [
    customerApplicationNotificationSchema,
    customerBookingNotificationSchema,
    customerBookingCancelledNotificationSchema,
    cleanerSelectedNotificationSchema,
    cleanerNotSelectedNotificationSchema,
    cleanerBookingCancelledNotificationSchema,
    customerCompletionReviewRequiredNotificationSchema,
    cleanerCompletionApprovedNotificationSchema,
    cleanerCompletionDisputedNotificationSchema,
    customerReviewReceivedNotificationSchema,
    cleanerReviewReceivedNotificationSchema,
    cleanerProviderNoShowReportedNotificationSchema,
    customerMessageNotificationSchema,
    cleanerMessageNotificationSchema,
    customerNegotiableBookingNotificationSchema,
    cleanerNegotiableBookingNotificationSchema,
    customerOfferReceivedNotificationSchema,
    cleanerOfferReceivedNotificationSchema,
    customerOfferAcceptedNotificationSchema,
    cleanerOfferAcceptedNotificationSchema,
    customerOfferDeclinedNotificationSchema,
    cleanerOfferDeclinedNotificationSchema,
    customerOfferWithdrawnNotificationSchema,
    cleanerOfferWithdrawnNotificationSchema,
    customerOfferClosedNotificationSchema,
    cleanerOfferClosedNotificationSchema,
    customerOfferExpiredNotificationSchema,
    cleanerOfferExpiredNotificationSchema,
  ])
  .superRefine((notification, context) => {
    if (
      notification.readAt !== null &&
      Date.parse(notification.readAt) < Date.parse(notification.createdAt)
    ) {
      context.addIssue({
        code: "custom",
        path: ["readAt"],
        message: "Notification read time must not precede creation",
      });
    }
  });

export const inboxNotificationListResponseSchema = z
  .strictObject({
    status: z.literal("ok"),
    notifications: z.array(inboxNotificationSchema).max(INBOX_NOTIFICATION_PAGE_LIMIT),
    nextCursor: inboxNotificationCursorSchema.nullable(),
    unreadCount: unreadCountSchema,
  })
  .superRefine((response, context) => {
    const ids = new Set<string>();
    for (const [index, notification] of response.notifications.entries()) {
      if (ids.has(notification.id)) {
        context.addIssue({
          code: "custom",
          path: ["notifications", index, "id"],
          message: "Notification IDs must be unique within a page",
        });
      }
      ids.add(notification.id);
    }

    const pageUnreadCount = response.notifications.filter(({ readAt }) => readAt === null).length;
    if (response.unreadCount < pageUnreadCount) {
      context.addIssue({
        code: "custom",
        path: ["unreadCount"],
        message: "Unread count must include unread notifications in the page",
      });
    }
  });

const visibleMarkInboxNotificationReadResponseSchema = z
  .strictObject({
    status: z.enum(["read", "existing"]),
    notification: inboxNotificationSchema,
    unreadCount: unreadCountSchema,
  })
  .superRefine((response, context) => {
    if (response.notification.readAt === null) {
      context.addIssue({
        code: "custom",
        path: ["notification", "readAt"],
        message: "A read response must contain a read notification",
      });
    }
  });

const unavailableMarkInboxNotificationReadResponseSchema = z.strictObject({
  status: z.literal("unavailable"),
  notificationId: inboxNotificationIdSchema,
  unreadCount: unreadCountSchema,
});

export const markInboxNotificationReadResponseSchema = z.union([
  visibleMarkInboxNotificationReadResponseSchema,
  unavailableMarkInboxNotificationReadResponseSchema,
]);

export const dismissInboxNotificationResponseSchema = z
  .strictObject({
    status: z.enum(["dismissed", "existing"]),
    notificationId: inboxNotificationIdSchema,
    readAt: isoDateTimeSchema,
    dismissedAt: isoDateTimeSchema,
    versionNo: versionNoSchema,
    unreadCount: unreadCountSchema,
  })
  .superRefine((response, context) => {
    if (Date.parse(response.dismissedAt) < Date.parse(response.readAt)) {
      context.addIssue({
        code: "custom",
        path: ["dismissedAt"],
        message: "Dismissal time must not precede read time",
      });
    }
  });

export type InboxNotificationCategoryCode = z.infer<typeof inboxNotificationCategoryCodeSchema>;
export type InboxNotificationTemplateCode = z.infer<typeof inboxNotificationTemplateCodeSchema>;
export type InboxNotification = z.infer<typeof inboxNotificationSchema>;
export type InboxNotificationListResponse = z.infer<typeof inboxNotificationListResponseSchema>;
export type MarkInboxNotificationReadResponse = z.infer<
  typeof markInboxNotificationReadResponseSchema
>;
export type DismissInboxNotificationResponse = z.infer<
  typeof dismissInboxNotificationResponseSchema
>;
